import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, SparklesIcon, FireIcon } from '@heroicons/react/24/outline';
import { TeaserDemoProps } from '../../config/teaserContent';
import { createOptimizedTeaserDemo, useTeaserDemoState } from './baseTeaserDemo';

interface LifecycleVisualizationDemoProps extends TeaserDemoProps {
  strategies?: Array<{
    name: string;
    stage: 'Emerging' | 'Growing' | 'Mature' | 'Declining';
    progress: number;
    allocation: number;
    performance: number;
  }>;
}

const LifecycleVisualizationDemoComponent: React.FC<LifecycleVisualizationDemoProps> = ({ 
  className, 
  aspectRatio = 'video',
  strategies = [
    { name: 'Momentum Pro', stage: 'Growing', progress: 75, allocation: 35, performance: 18.7 },
    { name: 'Elite Scalper', stage: 'Mature', progress: 95, allocation: 40, performance: 24.3 },
    { name: 'Grid Master', stage: 'Emerging', progress: 40, allocation: 15, performance: 12.1 },
    { name: 'Beta Trader', stage: 'Declining', progress: 25, allocation: 10, performance: -5.8 }
  ]
}) => {
  const { containerClass } = useTeaserDemoState(aspectRatio);

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'Emerging': return 'text-cyan-400 bg-cyan-500/20 border-cyan-500/40';
      case 'Growing': return 'text-green-400 bg-green-500/20 border-green-500/40';
      case 'Mature': return 'text-blue-400 bg-blue-500/20 border-blue-500/40';
      case 'Declining': return 'text-orange-400 bg-orange-500/20 border-orange-500/40';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/40';
    }
  };

  const getStageIcon = (stage: string) => {
    switch (stage) {
      case 'Emerging': return SparklesIcon;
      case 'Growing': return FireIcon;
      case 'Mature': return ClockIcon;
      case 'Declining': return ClockIcon;
      default: return ClockIcon;
    }
  };

  const getStageEmoji = (stage: string) => {
    switch (stage) {
      case 'Emerging': return '🌱';
      case 'Growing': return '🚀';
      case 'Mature': return '🏆';
      case 'Declining': return '⚠️';
      default: return '📊';
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
      className={`relative p-4 h-full flex flex-col justify-between ${containerClass} ${className}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <motion.div
          animate={{ 
            rotate: [0, 360],
            scale: [1, 1.1, 1]
          }}
          transition={{ 
            rotate: { duration: 8, repeat: Infinity, ease: "linear" },
            scale: { duration: 2, repeat: Infinity }
          }}
          className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg"
        >
          <ClockIcon className="w-4 h-4 text-white" />
        </motion.div>
        <div>
          <h4 className="text-white font-semibold text-sm">Lifecycle Stages</h4>
          <p className="text-gray-300 text-xs">{strategies.length} strategies tracked</p>
        </div>
      </div>

      {/* Strategy Lifecycle Cards */}
      <div className="space-y-3 flex-1">
        {strategies.map((strategy, index) => {
          const StageIcon = getStageIcon(strategy.stage);
          return (
            <motion.div
              key={strategy.name}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.2, duration: 0.5 }}
              className={`p-3 rounded-lg border backdrop-blur-sm ${getStageColor(strategy.stage)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{getStageEmoji(strategy.stage)}</span>
                  <div>
                    <p className="text-white font-medium text-sm">{strategy.name}</p>
                    <p className="text-xs opacity-80">{strategy.stage} Stage</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-bold ${strategy.performance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {strategy.performance >= 0 ? '+' : ''}{strategy.performance}%
                  </p>
                  <p className="text-xs opacity-80">{strategy.allocation}% allocated</p>
                </div>
              </div>

              {/* Lifecycle Progress Bar */}
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs opacity-80">Lifecycle Progress</span>
                  <span className="text-xs font-medium">{strategy.progress}%</span>
                </div>
                <div className="w-full bg-black/30 rounded-full h-2">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${strategy.progress}%` }}
                    transition={{ delay: index * 0.2 + 0.5, duration: 1.5, ease: "easeOut" }}
                    className={`h-full rounded-full ${
                      strategy.stage === 'Emerging' ? 'bg-gradient-to-r from-cyan-400 to-blue-400' :
                      strategy.stage === 'Growing' ? 'bg-gradient-to-r from-green-400 to-emerald-400' :
                      strategy.stage === 'Mature' ? 'bg-gradient-to-r from-blue-400 to-indigo-400' :
                      'bg-gradient-to-r from-orange-400 to-red-400'
                    }`}
                  />
                </div>
              </div>

              {/* Stage Indicator */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1">
                  <StageIcon className="w-3 h-3" />
                  <span className="text-xs font-medium">{strategy.stage}</span>
                </div>
                
                {/* Allocation Progress */}
                <motion.div
                  animate={{ 
                    scale: strategy.stage === 'Growing' ? [1, 1.1, 1] : 1,
                    opacity: strategy.stage === 'Declining' ? [1, 0.7, 1] : 1
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="text-xs font-medium"
                >
                  {strategy.allocation}%
                </motion.div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Lifecycle Summary */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 0.5 }}
        className="mt-3 grid grid-cols-2 gap-2 text-xs"
      >
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-2 text-center">
          <p className="text-green-400 font-bold">
            {strategies.filter(s => s.stage === 'Growing' || s.stage === 'Mature').length}
          </p>
          <p className="text-gray-400">Performing</p>
        </div>
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-2 text-center">
          <p className="text-cyan-400 font-bold">
            {strategies.filter(s => s.stage === 'Emerging').length}
          </p>
          <p className="text-gray-400">Emerging</p>
        </div>
      </motion.div>

      {/* Adaptive Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        className="mt-2 text-center"
      >
        <motion.p
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-pink-300 text-xs font-medium"
        >
          🔄 Adaptive lifecycle management
        </motion.p>
      </motion.div>
    </motion.div>
  );
};

export const LifecycleVisualizationDemo = createOptimizedTeaserDemo(
  LifecycleVisualizationDemoComponent,
  'LifecycleVisualizationDemo'
);